/**
 * The docked AI side panel's state (SellerMate W6.3).
 *
 * One app-wide store, so any grid can open the panel with its own context —
 * the page, the date range it shows and the rows the user selected — without
 * threading props through the shell:
 *
 *   openAiPanel({ context: { page: 'Keywords', dateRange, selection } })
 *   openAiPanel({ prompt: 'Which campaigns…' })     // fills the composer
 *
 * The context travels to the (read-only) Analytics Agent as a short preamble
 * in front of the question (`composeQuestion`); the user sees it as removable
 * chips (`contextChips`). Nothing here changes data.
 *
 * The page's date range travels WITH the question (`api.chatAnalytics` sends it
 * as `date_range`, PR 12b), and the agent adds PAGE RANGE sections for exactly
 * those days to its fixed latest-day / 7-day / 30-day ones
 * (backend/chat/agent_context.py). Removing the range chip stops sending it.
 * `AI_DATA_WINDOWS` tells the user which figures the assistant sees.
 */
import { useSyncExternalStore } from 'react'
import { fmtInt } from './format'

export type AiSelection = {
  /** Plural noun for what was selected: "keywords", "campaigns". */
  noun: string
  count: number
  /** Readable names of (up to) the first rows — the rest are counted. */
  labels: string[]
}

export type AiContext = {
  page?: string
  dateRange?: { from: string; to: string }
  selection?: AiSelection
}

/** What the assistant's figures cover, in plain words (shown in the panel). */
export const AI_DATA_WINDOWS =
  'The assistant sees ads data for the date range shown on this page (sent with your question), plus the latest full day, the 7 days before it and the last 30 days.'

/** The same line when NO date range is attached (a page without a date control, or the range chip removed):
 *  the assistant then answers from its fixed windows only. */
export const AI_DATA_WINDOWS_FIXED =
  'No date range is attached to this question, so the assistant uses the latest full day, the 7 days before it and the last 30 days.'

/** Which line the empty state shows: the page-range sentence only when a range really rides along. */
export function aiDataWindows(ctx: AiContext): string {
  return ctx.dateRange ? AI_DATA_WINDOWS : AI_DATA_WINDOWS_FIXED
}

/** The context the header's Ask AI button sends on a page that shows the date
 *  range (PR 12, C10): the page's name and the window ON SCREEN (the fetched
 *  one, cut to the reported days when the page clamps). */
export function pageRangeContext(page: string, window: { from: string; to: string }): AiContext {
  return { page, dateRange: { from: window.from, to: window.to } }
}

/** What rides with the question as fields (PR 12b, C10): the page's range and name, exactly the
 *  chips still on the panel. A removed chip is simply absent here, so it is not sent. */
export function chatOptions(ctx: AiContext): { date_range?: { from: string; to: string }; page?: string } {
  return {
    ...(ctx.dateRange ? { date_range: { from: ctx.dateRange.from, to: ctx.dateRange.to } } : {}),
    ...(ctx.page ? { page: ctx.page } : {}),
  }
}

export type AiChipKey = keyof AiContext
export type AiChip = { key: AiChipKey; label: string }

export type AiTab = 'ask' | 'prompts' | 'briefings'

export type AiPanelState = {
  open: boolean
  tab: AiTab
  draft: string
  context: AiContext
}

/** How many selected-row names ride along with a question. */
export const MAX_SELECTION_LABELS = 25

export function contextChips(ctx: AiContext): AiChip[] {
  const chips: AiChip[] = []
  if (ctx.page) chips.push({ key: 'page', label: ctx.page })
  if (ctx.dateRange) {
    const { from, to } = ctx.dateRange
    chips.push({ key: 'dateRange', label: from === to ? from : `${from} to ${to}` })
  }
  if (ctx.selection && ctx.selection.count > 0) {
    const { count, noun } = ctx.selection
    chips.push({ key: 'selection', label: `${fmtInt(count)} selected ${noun}` })
  }
  return chips
}

export function withoutChip(ctx: AiContext, key: AiChipKey): AiContext {
  const next = { ...ctx }
  delete next[key]
  return next
}

/** The question as sent: a context preamble the agent can use, then the
 *  user's words. Without context it is the question unchanged. */
export function composeQuestion(question: string, ctx: AiContext): string {
  const q = question.trim()
  const lines: string[] = []
  if (ctx.page) lines.push(`Page: ${ctx.page}`)
  // The range is also sent as a field (api.chatAnalytics), which is what makes the agent's
  // PAGE RANGE sections cover these days; the line keeps the question readable on its own.
  if (ctx.dateRange) lines.push(`Date range on screen: ${ctx.dateRange.from} to ${ctx.dateRange.to} (your PAGE RANGE sections cover these days)`)
  const sel = ctx.selection
  if (sel && sel.count > 0) {
    const shown = sel.labels.slice(0, MAX_SELECTION_LABELS)
    const more = sel.count - shown.length
    const list = shown.join('; ') + (more > 0 ? `; and ${more} more` : '')
    lines.push(`Selected ${sel.noun} (${sel.count}): ${list}`)
  }
  if (lines.length === 0) return q
  return `Context:\n${lines.map((l) => `- ${l}`).join('\n')}\n\nQuestion: ${q}`
}

/** Build a selection context from a grid's selected keys and its loaded
 *  rows. Keys whose row is not loaded (another page) are still counted. */
export function selectionFromRows<R>(
  selectedKeys: ReadonlySet<string>,
  rows: readonly R[],
  keyOf: (r: R) => string,
  labelOf: (r: R) => string,
  noun: string,
): AiSelection | undefined {
  if (selectedKeys.size === 0) return undefined
  const labels: string[] = []
  for (const r of rows) {
    if (labels.length >= MAX_SELECTION_LABELS) break
    if (selectedKeys.has(keyOf(r))) labels.push(labelOf(r))
  }
  return { noun, count: selectedKeys.size, labels }
}

// ── store ────────────────────────────────────────────────────────────────

let state: AiPanelState = { open: false, tab: 'ask', draft: '', context: {} }
const listeners = new Set<() => void>()

function set(next: Partial<AiPanelState>): void {
  state = { ...state, ...next }
  for (const l of listeners) l()
}

export function getAiPanelState(): AiPanelState {
  return state
}

export function openAiPanel(opts: { prompt?: string; context?: AiContext; tab?: AiTab } = {}): void {
  set({
    open: true,
    tab: opts.tab ?? (opts.prompt !== undefined || opts.context ? 'ask' : state.tab),
    draft: opts.prompt ?? state.draft,
    context: opts.context ?? state.context,
  })
}

export function closeAiPanel(): void {
  set({ open: false })
}

export function setAiTab(tab: AiTab): void {
  set({ tab })
}

export function setAiDraft(draft: string): void {
  set({ draft })
}

export function setAiContext(context: AiContext): void {
  set({ context })
}

/** Test hook: back to the initial state. */
export function resetAiPanel(): void {
  set({ open: false, tab: 'ask', draft: '', context: {} })
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => { listeners.delete(l) }
}

export function useAiPanel(): AiPanelState {
  return useSyncExternalStore(subscribe, getAiPanelState, getAiPanelState)
}
